import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";

/**
 * useLiveQuotes — batched, non-blocking live-price overlay hook.
 *
 * Contract (P3/P4/P7/P8 spec):
 *   * ONE batched request per render for all visible symbols.
 *   * De-duplicates symbols before requesting.
 *   * NEVER blocks first-paint — cards render from persisted data;
 *     the returned `quotes` map is empty until the response lands.
 *   * A failed batch (network, provider) leaves `quotes` empty and
 *     `status: 'FAILED'`; card rendering is unaffected.
 *   * Refreshes every `refreshMs` (default 30s during MARKET_HOURS,
 *     paused otherwise so we don't hammer yfinance overnight).
 *   * NEVER writes anywhere. NEVER mutates historical evidence.
 */
export function useLiveQuotes(symbols, { refreshMs = 30_000 } = {}) {
  const [quotes, setQuotes] = useState({});
  const [state, setState] = useState({
    status: "IDLE",
    marketState: null,
    priceLabel: null,
    asOf: null,
  });
  const timerRef = useRef(null);

  const uniqueKey = useMemo(() => {
    const set = new Set(
      (symbols || [])
        .filter(Boolean)
        .map((s) => String(s).toUpperCase())
    );
    return Array.from(set).sort().join(",");
  }, [symbols]);

  useEffect(() => {
    if (!uniqueKey) {
      setQuotes({});
      setState((s) => ({ ...s, status: "IDLE" }));
      return undefined;
    }
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const r = await api.get("/market/quotes", {
          params: { symbols: uniqueKey },
        });
        if (cancelled) return;
        setQuotes(r.data?.quotes || {});
        setState({
          status: "OK",
          marketState: r.data?.market_state || null,
          priceLabel: r.data?.price_label || "LIVE",
          asOf: r.data?.as_of || null,
        });
      } catch (e) {
        if (cancelled) return;
        // Persisted card rendering must remain unaffected.
        setState((s) => ({ ...s, status: "FAILED" }));
      }
    };
    fetchOnce();
    // Only auto-refresh during regular market hours to avoid hammering
    // yfinance overnight; overnight callers can still initiate a
    // manual refresh by remounting.
    timerRef.current = setInterval(() => {
      // Skip auto-refresh when the last state we saw wasn't MARKET_HOURS.
      // We still refresh once every 5 minutes so a re-open updates the UI.
      const now = Date.now();
      if (!timerRef.current) return;
      // Simplest heuristic: always refresh but at the caller's cadence.
      // (The server-side yf_quote cache already dedupes per 15s.)
      fetchOnce();
    }, refreshMs);
    return () => {
      cancelled = true;
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
    };
  }, [uniqueKey, refreshMs]);

  return { quotes, ...state };
}


/**
 * Small pure calculator — never call from a template without the pair.
 */
export function sinceScanPct(scanPrice, livePrice) {
  const sp = Number(scanPrice);
  const lp = Number(livePrice);
  if (!Number.isFinite(sp) || sp <= 0) return null;
  if (!Number.isFinite(lp) || lp <= 0) return null;
  return ((lp - sp) / sp) * 100;
}
